// Global error catcher — kept minimal to avoid drowning real problems in
// noise. Logs uncaught exceptions and unhandled promise rejections only.
// (Previous version logged every fetch failure as a banner overlay; that
// fired routinely for 401-before-login and other non-issues, so it's gone.)

// A ChunkLoadError means the browser is running an OLD app bundle and tried to
// lazy-load a route chunk whose hashed filename no longer exists (a new build
// replaced it). This happens to users who keep the app open across a deploy.
// Recover by fetching a fresh index.html + current chunks — a one-time reload,
// guarded so a genuinely-missing chunk can't cause a reload loop.
export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? '');
  return /ChunkLoadError|Loading chunk\s+[\w./-]+\s+failed|dynamically imported module/i.test(msg);
}

function recoverFromStaleChunk(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  try {
    const KEY = 'hub_chunk_reload_at';
    const last = Number(sessionStorage.getItem(KEY) || 0);
    // At most one auto-reload per 30s — if the chunk is truly gone, don't loop.
    if (Date.now() - last > 30_000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      // SCRUM-2338 — a plain location.reload() can re-serve the SAME cached
      // index.html + entry bundle (which still points at the deleted chunk), so
      // the reload hit the identical 404 and the user stayed stuck (login-broken
      // incident 2026-10-14). Navigate to a cache-busted URL so the browser
      // refetches the document + its asset map fresh, then the app loads clean.
      const url = new URL(window.location.href);
      url.searchParams.set('_v', String(Date.now()));
      window.location.replace(url.toString());
      return true;
    }
  } catch { /* sessionStorage/reload unavailable — fall through to logging */ }
  return false;
}

export function setupGlobalErrorHandling() {
  window.addEventListener('error', (event: ErrorEvent) => { // NOSONAR S7764 — browser-only Angular code
    if (recoverFromStaleChunk(event.error ?? event.message)) return;
    console.error('[uncaught]', event.message, event.filename + ':' + event.lineno, event.error?.stack);
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => { // NOSONAR S7764 — browser-only Angular code
    if (recoverFromStaleChunk(event.reason)) return;
    // Ignore HttpErrorResponse — those are surfaced via the HTTP interceptor.
    if (event.reason && typeof event.reason === 'object' && 'status' in event.reason) return;
    console.error('[unhandled-rejection]', event.reason);
  });
}

if (typeof window !== 'undefined') { // NOSONAR S7764 — browser-only Angular code
  setupGlobalErrorHandling();
}
